import { useEffect, useState, type FormEvent } from 'react';
import { MINDMAP_FARBEN, SPALTEN_TYP_NAMEN, SPALTEN_TYPEN, type MindmapFarbe, type MindmapSpalte, type SpaltenOption, type SpaltenTyp } from '@beholder/shared/module/mindmap';
import { Auswahl, Dialog, Eingabe, Feld, Knopf, Meldung, useAktion } from '../../core/ui';
import { FARB_NAMEN, FARBTON } from './symbole';
import type { KartenZugriff } from './useKarte';

const VORLAGEN: { name: string; typ: SpaltenTyp; optionen?: SpaltenOption[] }[] = [
  {
    name: 'Status',
    typ: 'auswahl',
    optionen: [
      { name: 'Offen', farbe: 'grau' },
      { name: 'In Arbeit', farbe: 'blau' },
      { name: 'Wartet', farbe: 'orange' },
      { name: 'Erledigt', farbe: 'gruen' },
    ],
  },
  { name: 'Verantwortlich', typ: 'person' },
  { name: 'Termin', typ: 'datum' },
  { name: 'Betrag', typ: 'betrag' },
  { name: 'Erledigt', typ: 'haken' },
  {
    name: 'Priorität',
    typ: 'auswahl',
    optionen: [
      { name: 'niedrig', farbe: 'grau' },
      { name: 'normal', farbe: 'blau' },
      { name: 'hoch', farbe: 'rot' },
    ],
  },
  { name: 'Link', typ: 'link' },
  { name: 'Fortschritt', typ: 'fortschritt' },
];

export function SpaltenDialog({ offen, spalte, z, schliessen }: { offen: boolean; spalte: MindmapSpalte | null; z: KartenZugriff; schliessen(): void }) {
  const [name, setName] = useState('');
  const [typ, setTyp] = useState<SpaltenTyp>('text');
  const [optionen, setOptionen] = useState<SpaltenOption[]>([]);
  const [waehrung, setWaehrung] = useState('EUR');
  const a = useAktion();

  useEffect(() => {
    if (!offen) return;
    setName(spalte?.name ?? '');
    setTyp(spalte?.typ ?? 'text');
    setOptionen(spalte?.optionen ?? []);
    setWaehrung(spalte?.waehrung ?? 'EUR');
    a.setFehler('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offen, spalte?.id]);

  const vorhanden = z.spalten.map((s) => s.name.toLowerCase());
  const index = spalte ? z.spalten.findIndex((s) => s.id === spalte.id) : -1;

  async function senden(e?: FormEvent) {
    e?.preventDefault();
    const ok = await a.ausfuehren(async () => {
      const opt = optionen.filter((o) => o.name.trim());
      if (spalte) await z.spalteAendern(spalte.id, { name, optionen: opt, waehrung: typ === 'betrag' ? waehrung : null });
      else await z.spalteAnlegen({ name, typ, optionen: opt, waehrung: typ === 'betrag' ? waehrung : null });
      return true;
    });
    if (ok) schliessen();
  }

  async function verschieben(d: number) {
    if (!spalte) return;
    await a.ausfuehren(() => z.spalteAendern(spalte.id, { index: Math.max(0, index + d) }));
  }

  async function loeschen() {
    if (!spalte || !confirm(`Spalte „${spalte.name}“ mit allen Werten löschen?`)) return;
    const ok = await a.ausfuehren(async () => {
      await z.spalteLoeschen(spalte.id);
      return true;
    });
    if (ok) schliessen();
  }

  return (
    <Dialog
      titel={spalte ? `Spalte „${spalte.name}“` : 'Neue Spalte'}
      offen={offen}
      schliessen={schliessen}
      fuss={
        <>
          <Knopf art="haupt" onClick={() => void senden()} disabled={a.laeuft || !name.trim()}>
            {spalte ? 'Speichern' : 'Anlegen'}
          </Knopf>
          <Knopf onClick={schliessen}>Abbrechen</Knopf>
          {spalte && (
            <>
              <span className="abstand" />
              <Knopf art="leise" onClick={() => void verschieben(-1)} disabled={index <= 0} title="Nach links">
                ←
              </Knopf>
              <Knopf art="leise" onClick={() => void verschieben(1)} disabled={index >= z.spalten.length - 1} title="Nach rechts">
                →
              </Knopf>
              <Knopf art="gefahr" onClick={() => void loeschen()}>
                Löschen
              </Knopf>
            </>
          )}
        </>
      }
    >
      <form className="stapel" onSubmit={senden}>
        <Meldung>{a.fehler}</Meldung>
        {!spalte && (
          <div className="reihe">
            <span className="leise klein">Vorlagen:</span>
            {VORLAGEN.filter((v) => !vorhanden.includes(v.name.toLowerCase())).map((v) => (
              <Knopf
                key={v.name}
                klein
                onClick={() => {
                  setName(v.name);
                  setTyp(v.typ);
                  setOptionen(v.optionen ?? []);
                }}
              >
                {v.name}
              </Knopf>
            ))}
          </div>
        )}
        <Feld titel="Name">
          <Eingabe value={name} onChange={(e) => setName(e.target.value)} maxLength={100} />
        </Feld>
        <Feld titel="Art" hinweis={spalte ? 'Die Art lässt sich nachträglich nicht ändern.' : undefined}>
          <Auswahl value={typ} onChange={(e) => setTyp(e.target.value as SpaltenTyp)} disabled={!!spalte}>
            {SPALTEN_TYPEN.map((t) => (
              <option key={t} value={t}>
                {SPALTEN_TYP_NAMEN[t]}
              </option>
            ))}
          </Auswahl>
        </Feld>
        {typ === 'betrag' && (
          <Feld titel="Währung">
            <Auswahl value={waehrung} onChange={(e) => setWaehrung(e.target.value)}>
              {['EUR', 'USD', 'CHF', 'GBP', 'JPY', 'PLN', 'CZK', 'SEK', 'DKK', 'NOK', 'HUF', 'TRY'].map((w) => (
                <option key={w}>{w}</option>
              ))}
            </Auswahl>
          </Feld>
        )}
        {typ === 'auswahl' && (
          <fieldset className="mm-optionen">
            <legend>Möglichkeiten</legend>
            {optionen.map((o, i) => (
              <div key={i} className="mm-option-zeile">
                <Eingabe
                  value={o.name}
                  maxLength={100}
                  onChange={(e) => setOptionen(optionen.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))}
                  aria-label="Name der Möglichkeit"
                />
                <Auswahl
                  value={o.farbe ?? ''}
                  onChange={(e) => setOptionen(optionen.map((x, j) => (j === i ? { ...x, farbe: (e.target.value || null) as MindmapFarbe | null } : x)))}
                  aria-label="Farbe"
                  style={o.farbe ? { color: FARBTON[o.farbe] } : undefined}
                >
                  <option value="">ohne Farbe</option>
                  {MINDMAP_FARBEN.map((f) => (
                    <option key={f} value={f}>
                      {FARB_NAMEN[f]}
                    </option>
                  ))}
                </Auswahl>
                <Knopf klein art="leise" aria-label="Entfernen" onClick={() => setOptionen(optionen.filter((_, j) => j !== i))}>
                  ✕
                </Knopf>
              </div>
            ))}
            <Knopf klein onClick={() => setOptionen([...optionen, { name: '', farbe: null }])}>
              + Möglichkeit
            </Knopf>
          </fieldset>
        )}
      </form>
    </Dialog>
  );
}
